import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";
import OnboardingVideo from "@/components/OnboardingVideo";
import { HeaderTextBlock, P } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useGetKlaviyoApiKey,
  usePostKlaviyoPrivateKey,
  usePostVerifyKlaviyoApiKey,
} from "@/requests/hooks/collect";
import { KLAVIYO_CREATE_API_KEY_URL, NAVIGATE_FORWARDS } from "@/utils/const";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InferType, object, string } from "yup";
import debounce from "lodash/debounce";
import Shimmer from "@/components/Shimmer";
import createKlaviyoPrivateApiKey from "@/assets/videos/collect/onboarding/create-a-private-api-key-in-klaviyo.mp4";

import klaviyoLogo from "@/assets/svgs/klaviyo-logo.svg";

let hasFormBeenSubmitted = false;

const formSchema = object({
  klaviyoApiKey: string()
    .required(t("emailOnboarding.klaviyoApiKey.right.input.blankPrivateKey"))
    .test({
      test: value =>
        value.startsWith("**********") ||
        value.startsWith("pk_") ||
        value.startsWith("PK_"),
      message: t("emailOnboarding.klaviyoApiKey.right.input.invalidPrivateKey"),
    }),
});

type IKlaviyoApiKeyForm = InferType<typeof formSchema>;

const KlaviyoApiKey = () => {
  const navigate = useNavigate();
  const [isPrivateKeyPresent, setIsPrivateKeyPresent] =
    useState<boolean>(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    trigger,
    setValue,
    getValues,
  } = useForm<IKlaviyoApiKeyForm>({
    resolver: yupResolver(formSchema),
  });

  const {
    data: verifyKlaviyoApiKey,
    error: verifyKlaviyoApiKeyError,
    isPending: isFetchingVerifyKlaviyoApiKey,
  } = usePostVerifyKlaviyoApiKey({
    privateKey: getValues("klaviyoApiKey"),
    enabled: isPrivateKeyPresent,
  });

  const {
    isPending: isFetchingSendKlaviyoPrivateKey,
    mutate: sendKlaviyoPrivateKey,
    isSuccess: isSendKlaviyoPrivateKeySuccess,
  } = usePostKlaviyoPrivateKey();

  const {
    data: savedKlaviyoApiKey,
    refetch,
    isLoading: isFetchingKlaviyoApiKey,
  } = useGetKlaviyoApiKey();

  useEffect(() => {
    if (savedKlaviyoApiKey) {
      setValue("klaviyoApiKey", savedKlaviyoApiKey?.privateApiKey);
    }
  }, [savedKlaviyoApiKey]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      trigger();
    }
  }, [watch("klaviyoApiKey")]);

  useEffect(() => {
    const debouncedCb = debounce(formValue => {
      setIsPrivateKeyPresent(
        formValue.klaviyoApiKey.includes("pk_") ||
          formValue.klaviyoApiKey.includes("PK_"),
      );
    }, 1000);

    const subscription = watch(debouncedCb);

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (isSendKlaviyoPrivateKeySuccess) {
      refetch();
      navigateToNextScreen();
    }
  }, [isSendKlaviyoPrivateKeySuccess]);

  const navigateToNextScreen = () => {
    navigate(getOnboardingUrl("KLAVIYO_ENABLED"), {
      state: {
        direction: NAVIGATE_FORWARDS,
      },
    });
  };

  const onSubmitPrivateKey = async ({ klaviyoApiKey }: IKlaviyoApiKeyForm) => {
    if (savedKlaviyoApiKey?.privateApiKey === klaviyoApiKey) {
      navigateToNextScreen();
      return;
    }
    if (!hasFormBeenSubmitted) hasFormBeenSubmitted = true;
    const isValid = await trigger();
    if (!isValid) return;
    sendKlaviyoPrivateKey(klaviyoApiKey);
  };

  const renderVideo = () => (
    <OnboardingVideo videoLocation={createKlaviyoPrivateApiKey} />
  );

  const renderInput = () => (
    <Form onSubmit={handleSubmit(onSubmitPrivateKey)} id="klaviyo-api-key-form">
      <Input
        {...register("klaviyoApiKey")}
        testId="klaviyo-api-key-input"
        label={t("emailOnboarding.klaviyoApiKey.right.input.label")}
        placeholder={t("emailOnboarding.klaviyoApiKey.right.input.label")}
        helperText={errors?.klaviyoApiKey?.message}
        variant={errors?.klaviyoApiKey?.message ? "error" : undefined}
      />
    </Form>
  );

  const renderCta = () => {
    const formApiKey = getValues("klaviyoApiKey");

    const isDisabled =
      !!errors?.klaviyoApiKey?.message ||
      (!formApiKey && !savedKlaviyoApiKey) ||
      (!formApiKey && !verifyKlaviyoApiKey) ||
      (isPrivateKeyPresent && isFetchingVerifyKlaviyoApiKey) ||
      !!verifyKlaviyoApiKeyError;

    return (
      <div className="md:max-w-[180px]">
        <Button
          variant="primary"
          trackingId={{
            eventName: "onboarding.collect.klaviyo.submit-private-key",
          }}
          type="submit"
          form="klaviyo-api-key-form"
          loading={isFetchingSendKlaviyoPrivateKey || isFetchingKlaviyoApiKey}
          disabled={isDisabled}
          width="sm"
          testId="submit-klaviyo-api-key">
          {t("emailOnboarding.klaviyoApiKey.right.cta")}
        </Button>
      </div>
    );
  };

  const renderOrganizationConfirmation = () => {
    if (isPrivateKeyPresent && isFetchingVerifyKlaviyoApiKey)
      return <Shimmer height={100} />;

    if (!!verifyKlaviyoApiKeyError || !!verifyKlaviyoApiKey?.organizationName) {
      return (
        <Alert
          variant={verifyKlaviyoApiKeyError ? "error" : "info"}
          titleSlot={
            <P>
              {t("emailOnboarding.klaviyoApiKey.right.klaviyoOrganization")}
            </P>
          }
          icon={klaviyoLogo}
          iconClassName="w-20 h-20">
          <P className="font-bold">
            {!!verifyKlaviyoApiKey?.organizationName
              ? verifyKlaviyoApiKey?.organizationName
              : t(
                  "emailOnboarding.klaviyoApiKey.right.klaviyoOrganizationError",
                )}
          </P>
        </Alert>
      );
    }

    return null;
  };

  const renderInputAndButton = () => (
    <div className="flex flex-col gap-30">
      <div className="mt-30 flex flex-row flex-wrap items-start gap-20 md:flex-nowrap">
        <div className="w-full flex-grow md:w-[unset]">{renderInput()}</div>
        <div className="w-full flex-shrink-0 pt-[unset] md:w-[unset] md:pt-5 ">
          {renderCta()}
        </div>
      </div>
      {renderOrganizationConfirmation()}
    </div>
  );

  const renderRightPanel = () => (
    <>
      {renderVideo()}
      {renderInputAndButton()}
    </>
  );

  const renderDescriptionText = () => (
    <ol className="flex list-decimal flex-col gap-10 ps-20 text-start text-primary-dark">
      <li>
        <div className="gap-5">
          <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step1">
            zero
            <Button
              as="a"
              href={KLAVIYO_CREATE_API_KEY_URL}
              trackingId={{
                eventName: "onboarding.collect.klaviyo.go-to-private-key",
              }}
              variant="link"
              width="unset"
              icon={faCaretRight}
              iconPosition="end"
              iconClassName="w-15 h-15">
              one
            </Button>
            two
          </Trans>
        </div>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step2">
          one
          <span className="font-bold">two</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step3">
          one
          <span className="font-bold">two</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step4">
          Zero
          <span className="font-bold">one</span>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step5">
          zero
          <span className="font-bold">one</span>
          two
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.klaviyoApiKey.left.header.description.step6">
          zero
          <span className="font-bold">one</span>
        </Trans>
      </li>
    </ol>
  );

  const renderHeaderTextBlock = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.klaviyoApiKey.left.header.feature")}
      featureTextColor="primary"
      headingText={t("emailOnboarding.klaviyoApiKey.left.header.heading")}
      headingTextColor="secondary"
      customHtml={renderDescriptionText()}
      customHtmlPosition="bottom"
    />
  );

  return (
    <SplitLayout
      leftPanel={renderHeaderTextBlock()}
      rightPanel={renderRightPanel()}
    />
  );
};

export default KlaviyoApiKey;
